import { RRule } from "rrule";
import {
  getArchitectPrompts,
  getCallRoutes,
  getDIDNumbers,
  getFlows,
  getGroups,
  getGroupMembers,
  getGroupRoles,
  getIntegrations,
  getOAuths,
  getPermissions,
  getQueueWrapupCodes,
  getQueues,
  getRoles,
  getScheduleGroups,
  getScheduleGroupStatus,
  getSchedules,
  getWrapupCodes,
  searchUsers,
} from "./genesysCloudUtils";
import { EXPORT_METADATA_COLUMNS } from "./csvExportUtils";
import { fetchAllPages, mapWithConcurrency, unsupportedExport } from "./exportFetchUtils";
import { formatResourceConditionNode } from "./conditionFormatUtils";
import { permissionsToCheck } from "./helperUtils";

const metadataColumns = EXPORT_METADATA_COLUMNS;

export const EXPORT_ADAPTERS = {
  "divisions-tab": {
    label: "Divisions",
    columns: [
      { key: "division_name", header: "Division Name" },
      { key: "division_id", header: "Division ID" },
      ...metadataColumns,
    ],
    fetchAll: async ({ divisions }) => ({ entities: divisions || [], capped: false }),
    mapRecord: (division) => ({
      division_name: division.name,
      division_id: division.id,
    }),
  },
  "queues-tab": {
    label: "Queues",
    columns: [
      { key: "queue_name", header: "Queue Name" },
      { key: "description", header: "Description" },
      { key: "division_name", header: "Division" },
      { key: "acw_timeout", header: "ACW/Timeout" },
      { key: "voice_auto_answer", header: "Voice: Auto Answer" },
      { key: "voice_alerting_timeout", header: "Voice: Alerting Timeout" },
      { key: "voice_service_level", header: "Voice: Service Level" },
      { key: "voice_calling_party", header: "Voice: Calling Party" },
      { key: "voice_transcription", header: "Voice: Transcription" },
      { key: "callback_auto_answer", header: "Callback: Auto Answer" },
      { key: "callback_alerting_timeout", header: "Callback: Alerting Timeout" },
      { key: "callback_mode", header: "Callback: Mode" },
      { key: "callback_auto_dial_and_end", header: "Callback: Auto Dial and End" },
      { key: "callback_service_level", header: "Callback: Service Level" },
      { key: "message_auto_answer", header: "Message: Auto Answer" },
      { key: "message_alerting_timeout", header: "Message: Alerting Timeout" },
      { key: "message_service_level", header: "Message: Service Level" },
      { key: "email_auto_answer", header: "Email: Auto Answer" },
      { key: "email_alerting_timeout", header: "Email: Alerting Timeout" },
      { key: "email_service_level", header: "Email: Service Level" },
      { key: "email_address", header: "Email Address" },
      { key: "wrapup_codes", header: "Wrapup Codes" },
      { key: "queue_id", header: "QueueID" },
      ...metadataColumns,
    ],
    fetchAll: ({ checkedDivisions }) =>
      fetchAllPages({
        pageSize: 100,
        fetchPage: ({ pageNumber, pageSize }) =>
          getQueues({
            pageSize,
            pageNumber,
            sortOrder: "ASC",
            divisionId: checkedDivisions,
          }),
      }),
    enrichRecords: enrichQueuesWithWrapupCodes,
    mapRecord: (queue) => ({
      queue_name: queue.name,
      description: queue.description,
      division_name: queue.division?.name,
      acw_timeout: formatAcwTimeout(queue.acwSettings),
      voice_auto_answer: formatOnOff(queue.mediaSettings?.call?.enableAutoAnswer),
      voice_alerting_timeout: formatSeconds(queue.mediaSettings?.call?.alertingTimeoutSeconds),
      voice_service_level: formatServiceLevel(queue.mediaSettings?.call?.serviceLevel),
      voice_calling_party: [queue.callingPartyName, queue.callingPartyNumber].filter(Boolean).join(" "),
      voice_transcription: queue.enableTranscription ? "Enabled" : "Disabled",
      callback_auto_answer: formatOnOff(queue.mediaSettings?.callback?.enableAutoAnswer),
      callback_alerting_timeout: formatSeconds(queue.mediaSettings?.callback?.alertingTimeoutSeconds),
      callback_mode: queue.mediaSettings?.callback?.mode,
      callback_auto_dial_and_end: formatOnOff(queue.mediaSettings?.callback?.enableAutoDialAndEnd),
      callback_service_level: formatServiceLevel(queue.mediaSettings?.callback?.serviceLevel),
      message_auto_answer: formatOnOff(queue.mediaSettings?.message?.enableAutoAnswer),
      message_alerting_timeout: formatSeconds(queue.mediaSettings?.message?.alertingTimeoutSeconds),
      message_service_level: formatServiceLevel(queue.mediaSettings?.message?.serviceLevel),
      email_auto_answer: formatOnOff(queue.mediaSettings?.email?.enableAutoAnswer),
      email_alerting_timeout: formatSeconds(queue.mediaSettings?.email?.alertingTimeoutSeconds),
      email_service_level: formatServiceLevel(queue.mediaSettings?.email?.serviceLevel),
      email_address: formatOutboundEmailAddress(queue.outboundEmailAddress),
      wrapup_codes: (queue.wrapUpCodes || []).map((code) => code.name).filter(Boolean).join(", "),
      queue_id: queue.id,
    }),
  },
  "groups-tab": {
    label: "Groups",
    columns: [
      { key: "record_type", header: "Record Type" },
      { key: "group_name", header: "Group Name" },
      { key: "description", header: "Description" },
      { key: "role_entity_name", header: "Roles-Entity Name" },
      { key: "division_name", header: "Division" },
      { key: "member_entity_name", header: "Member -Entity Name" },
      { key: "owner", header: "Owner" },
      { key: "calls_enabled", header: "Call Enabled" },
      { key: "group_id", header: "UID" },
      ...metadataColumns,
    ],
    fetchAll: () =>
      fetchAllPages({
        pageSize: 100,
        fetchPage: ({ pageNumber, pageSize }) =>
          getGroups({ pageSize, pageNumber, sortOrder: "ASC" }),
      }),
    enrichRecords: enrichGroupsForExport,
    mapRecord: mapGroupForExport,
  },
  "roles-tab": {
    label: "Roles",
    columns: [
      { key: "role_name", header: "Role Name" },
      { key: "role_description", header: "Description" },
      { key: "default", header: "Default" },
      { key: "permissions_list", header: "Permissions List" },
      { key: "permissions", header: "Permission" },
      { key: "domain", header: "Domain" },
      { key: "entity_name", header: "Entity Name" },
      { key: "action_set", header: "Action Set" },
      { key: "conditions", header: "Conditions" },
      { key: "role_id", header: "Role ID" },
      ...metadataColumns,
    ],
    fetchAll: () =>
      fetchAllPages({
        pageSize: 100,
        fetchPage: ({ pageNumber, pageSize }) => getRoles({ pageSize, pageNumber }),
      }),
    mapRecord: (role) => {
      const policies = [...(role.permissionPolicies || [])].sort(
        (a, b) => a.domain.localeCompare(b.domain) || a.entityName.localeCompare(b.entityName)
      );
      const permissionEntries = policies.flatMap((policy) => {
        const actions = policy.actionSet?.length ? [...policy.actionSet].sort() : [""];
        const policyEntity = policy.entityName === "*" ? "All Permissions" : policy.entityName;

        return actions.map((action) => {
          const policyAction = action === "*" ? "All Actions" : action;
          return {
            permissions: [policy.domain, policyEntity, policyAction].filter(Boolean).join(" > "),
            domain: policy.domain,
            entity_name: policyEntity,
            action_set: policyAction,
            conditions: formatResourceConditionNode(policy.resourceConditionNode),
          };
        });
      }).filter((entry) => entry.permissions);

      const permissionsList = permissionEntries.map((entry) => entry.permissions).join(", ");

      if (!permissionEntries.length) {
        return [{
          role_name: role.name,
          role_description: role.description,
          default: role.default ? true : false,
          permissions_list: "",
          permissions: "",
          domain: "",
          entity_name: "",
          action_set: "",
          conditions: "",
          role_id: role.id,
        }];
      }

      return permissionEntries.map((entry, index) => ({
        role_name: index === 0 ? role.name : "",
        role_description: index === 0 ? role.description : "",
        default: index === 0 ? (role.default ? true : false) : "",
        permissions_list: index === 0 ? permissionsList : "",
        permissions: entry.permissions,
        domain: entry.domain,
        entity_name: entry.entity_name,
        action_set: entry.action_set,
        conditions: entry.conditions,
        role_id: index === 0 ? role.id : "",
      }));
    },
  },
  "permissions-tab": {
    label: "Permissions",
    columns: [
      { key: "domain", header: "Domain" },
      { key: "entity_type", header: "Entity Type" },
      { key: "action", header: "Action" },
      { key: "description", header: "Description" },
      { key: "allows_conditions", header: "Allow Conditions?" },
      { key: "division_aware", header: "Division-Aware?" },
      ...metadataColumns,
    ],
    fetchAll: async () => {
      const result = await fetchAllPages({
        pageSize: 2000,
        fetchPage: ({ pageNumber, pageSize }) => getPermissions({ pageSize, pageNumber }),
      });
      return {
        ...result,
        entities: result.entities.flatMap((permission) =>
          Object.values(permission.permissionMap || {}).flat()
        ),
      };
    },
    mapRecord: (permission) => ({
      domain: permission.domain,
      entity_type: permission.entityType,
      action: permission.action,
      description: permission.label,
      allows_conditions: permission.allowsConditions ? "true" : "false",
      division_aware: permission.divisionAware ? "true" : "false",
    }),
  },
  "wrapup-codes-tab": {
    label: "Wrapup Codes",
    columns: [
      { key: "wrapup_code_name", header: "Wrap-Up Code" },
      { key: "division_name", header: "Division" },
      { key: "wrapup_code_id", header: "Wrap-Up Code ID" },
      ...metadataColumns,
    ],
    fetchAll: () =>
      fetchAllPages({
        pageSize: 500,
        fetchPage: ({ pageNumber, pageSize }) =>
          getWrapupCodes({ pageSize, pageNumber, sortBy: "name", sortOrder: "ascending" }),
      }),
    mapRecord: (wrapupCode) => ({
      wrapup_code_name: wrapupCode.name,
      division_name: wrapupCode.division?.name || "",
      wrapup_code_id: wrapupCode.id,
    }),
  },
  "schedules-tab": {
    label: "Schedules",
    columns: [
      { key: "schedule_name", header: "Schedule Name" },
      { key: "start_time", header: "Start Time" },
      { key: "end_time", header: "End Time" },
      { key: "evaluated_rule", header: "Evaluated Rule" },
      { key: "schedule_rrule", header: "Schedule Rrule" },
      { key: "division_name", header: "Division" },
      { key: "schedule_id", header: "Schedule ID" },
      ...metadataColumns,
    ],
    fetchAll: ({ checkedDivisions }) =>
      fetchAllPages({
        pageSize: 100,
        fetchPage: ({ pageNumber, pageSize }) =>
          getSchedules({
            pageSize,
            pageNumber,
            sortBy: "name",
            sortOrder: "ASC",
            divisionId: checkedDivisions,
          }),
      }),
    mapRecord: (schedule) => ({
      schedule_name: schedule.name,
      start_time: schedule.start ? schedule.start.slice(11, 16) : "",
      end_time: schedule.end ? schedule.end.slice(11, 16) : "",
      evaluated_rule: schedule.rrule
        ? RRule.fromString(schedule.rrule).toText()
        : [schedule.start?.split("T")[0], schedule.end?.split("T")[0]].filter(Boolean).join(" to "),
      schedule_rrule: schedule.rrule || "",
      division_name: schedule.division?.name || "",
      schedule_id: schedule.id,
    }),
  },
  "users-tab": {
    label: "Users",
    columns: [
      { key: "user_name", header: "User" },
      { key: "email", header: "Email" },
      { key: "division_name", header: "Division" },
      { key: "roles_count", header: "Role Count" },
      { key: "last_login", header: "Last Login" },
      { key: "has_station", header: "Has Station" },
      { key: "acd_auto_answer", header: "ACD Auto Answer" },
      { key: "team_name", header: "Team" },
      { key: "phone", header: "Phone Number" },
      { key: "user_id", header: "User ID" },
      ...metadataColumns,
    ],
    fetchAll: ({ filters }) =>
      fetchAllPages({
        pageSize: 100,
        fetchPage: ({ pageNumber, pageSize }) => searchUsers(buildUserSearchBody(filters, pageNumber, pageSize)),
      }),
    mapRecord: (user) => ({
      user_name: user.name,
      email: user.email,
      division_name: user.division?.name,
      roles_count: user.authorization?.roles?.length || 0,
      last_login: user.dateLastLogin ? new Date(user.dateLastLogin).toLocaleString() : "",
      has_station: Boolean(user.station),
      acd_auto_answer: user.acdAutoAnswer ? "on" : "off",
      team_name: user.team?.name,
      phone: user.primaryContactInfo?.find((contact) => contact.mediaType === "PHONE")?.display,
      user_id: user.id,
    }),
  },
  "flows-tab": {
    label: "Flows",
    columns: [
      { key: "flow_name", header: "Flow Name" },
      { key: "flow_type", header: "Flow Type" },
      { key: "flow_languages", header: "Languages" },
      { key: "division_name", header: "Division" },
      { key: "flow_id", header: "Flow ID" },
      ...metadataColumns,
    ],
    fetchAll: ({ checkedDivisions, filters = {} }) =>
      fetchAllPages({
        pageSize: 100,
        fetchPage: ({ pageNumber, pageSize }) =>
          getFlows({
            pageSize,
            pageNumber,
            sortBy: "name",
            sortOrder: "ASC",
            divisionId: checkedDivisions,
            ...(filters.name ? { name: `*${filters.name}*` } : {}),
            ...(filters.type?.type?.length ? { type: filters.type.type } : {}),
          }),
      }),
    mapRecord: (flow) => ({
      flow_name: flow.name,
      flow_type: flow.type,
      flow_languages: flow.publishedVersion?.supportedLanguages ? (
        flow.publishedVersion.supportedLanguages.map((lang) => (
          lang.language
        )).join(", ")
      ) : "",
      division_name: flow.division?.name,
      flow_id: flow.id,
    }),
  },
  "policies-tab": {
    label: "Policies",
    fetchAll: unsupportedExport("Policy export needs the existing recording policy backend permission mode wired into the export context first."),
  },
  "call-routes-tab": {
    label: "Call Routes",
    columns: [
      { key: "route_name", header: "Route Name" },
      { key: "division_name", header: "Division" },
      { key: "open_flow_name", header: "Flow - Open" },
      { key: "dnis_list", header: "DNIS" },
      { key: "route_id", header: "Route ID" },
      ...metadataColumns,
    ],
    fetchAll: ({ filters, divisions }) =>
      fetchAllPages({
        pageSize: 100,
        fetchPage: ({ pageNumber, pageSize }) =>
          getCallRoutes(buildCallRoutesOpts(filters, pageNumber, pageSize), divisions || []),
      }),
    mapRecord: (route) => ({
      route_name: route.name,
      division_name: route.division?.name,
      open_flow_name: route.openHoursFlow?.name,
      dnis_list: (route.dnis || []).map((dnis) => dnis.split("+1")[1] || dnis).sort((a, b) => a.localeCompare(b, undefined, { numeric: true })),
      route_id: route.id,
    }),
  },
  "dids-tab": {
    label: "DIDs",
    columns: [
      { key: "did_number", header: "DID Number" },
      { key: "owner_name", header: "Association" },
      { key: "owner_id", header: "Owner ID" },
      ...metadataColumns,
    ],
    fetchAll: ({ filters, opts }) =>
      fetchAllPages({
        pageSize: 100,
        fetchPage: ({ pageNumber, pageSize }) =>
          getDIDNumbers({
            pageSize,
            pageNumber,
            sortBy: "phoneNumber",
            sortOrder: "ASC",
            ...(filters?.numberMatch ? { numberMatch: filters.numberMatch } : opts),
          }),
      }),
    mapRecord: (did) => ({
      did_number: did.number,
      owner_name: did.owner?.name,
      owner_id: did.owner?.id,
    }),
  },
  "oauths-tab": {
    label: "OAuths",
    columns: [
      { key: "oauth_name", header: "OAuth Name" },
      { key: "grant_type", header: "Grant Type" },
      { key: "redirect_uris", header: "Redirect URI" },
      { key: "client_id", header: "Client ID" },
      ...metadataColumns,
    ],
    fetchAll: async () => {
      const data = await getOAuths();
      return { entities: data.entities || [], capped: false };
    },
    mapRecord: (oauth) => ({
      oauth_name: oauth.name,
      grant_type: oauth.authorizedGrantType,
      redirect_uris: oauth.registeredRedirectUri,
      client_id: oauth.id,
    }),
  },
  "integrations-tab": {
    label: "Integrations",
    columns: [
      { key: "integration_name", header: "Integration Name" },
      { key: "state_code", header: "State" },
      { key: "last_updated", header: "Last Updated" },
      { key: "integration_id", header: "Integration ID" },
      ...metadataColumns,
    ],
    fetchAll: async () => {
      const data = await getIntegrations();
      return { entities: data.entities || [], capped: false };
    },
    mapRecord: (integration) => ({
      integration_name: integration.name,
      state_code: integration.reportedState?.code,
      last_updated: integration.reportedState?.lastUpdated,
      integration_id: integration.id,
      audit_last_modified: integration.reportedState?.lastUpdated,
    }),
  },
  "prompts-tab": {
    label: "Prompts",
    columns: [
      { key: "prompt_name", header: "Prompt Name" },
      { key: "description", header: "Description" },
      { key: "resource_language", header: "Resource Language" },
      { key: "resource_tts", header: "Resource TTS" },
      { key: "resource_text", header: "Resource Text" },
      { key: "resource_duration_seconds", header: "Resource Duration (s)" },
      { key: "resource_media_uri", header: "Resource Media URI" },
      { key: "prompt_id", header: "Prompt ID" },
      ...metadataColumns,
    ],
    fetchAll: ({ filters }) =>
      fetchAllPages({
        pageSize: 500,
        fetchPage: ({ pageNumber, pageSize }) =>
          getArchitectPrompts(buildPromptsOpts(filters, pageNumber, pageSize)),
      }),
    mapRecord: (prompt) => {
      const promptFields = {
        prompt_name: prompt.name,
        description: prompt.description,
        prompt_id: prompt.id,
      };
      const resources = prompt.resources || [];

      if (resources.length === 0) {
        return [promptFields];
      }

      return resources.map((resource) => ({
        ...promptFields,
        resource_language: resource.language,
        resource_tts: resource.ttsString,
        resource_text: resource.text,
        resource_duration_seconds: resource.durationSeconds,
        resource_media_uri: resource.mediaUri,
      }));
    },
  },
  "data-tables-tab": {
    label: "Data Tables",
    fetchAll: unsupportedExport("Data Tables export needs dynamic schema row fan-out in the next implementation pass."),
  },
  "schedule-groups-tab": {
    label: "Schedule Groups",
    columns: [
      { key: "schedule_group_name", header: "Schedule Group Name" },
      { key: "open_schedules", header: "Open" },
      { key: "closed_schedules", header: "Closed" },
      { key: "holiday_schedules", header: "Holiday" },
      { key: "active", header: "Active" },
      { key: "current", header: "Current" },
      { key: "timezone", header: "Timezone" },
      { key: "division_name", header: "Division" },
      { key: "schedule_group_id", header: "Schedule GroupID" },
      ...metadataColumns,
    ],
    fetchAll: ({ checkedDivisions }) =>
      fetchAllPages({
        pageSize: 100,
        fetchPage: ({ pageNumber, pageSize }) =>
          getScheduleGroups({
            pageSize,
            pageNumber,
            sortBy: "name",
            sortOrder: "ASC",
            divisionId: checkedDivisions,
          }),
      }),
    enrichRecords: enrichScheduleGroupsForExport,
    mapRecord: (scheduleGroup) => ({
      schedule_group_name: scheduleGroup.name,
      open_schedules: formatScheduleNames(scheduleGroup.openSchedules),
      closed_schedules: formatScheduleNames(scheduleGroup.closedSchedules),
      holiday_schedules: formatScheduleNames(scheduleGroup.holidaySchedules),
      active: formatScheduleGroupActive(scheduleGroup),
      current: scheduleGroup.timeZone ? new Date().toLocaleString("en-US", { timeZone: scheduleGroup.timeZone }) : "",
      timezone: scheduleGroup.timeZone,
      division_name: scheduleGroup.division?.name,
      schedule_group_id: scheduleGroup.id,
    }),
  },
  "wrapup-table-tab": {
    label: "Wrapup Table",
    fetchAll: async ({ checkedDivisions }) => {
      const [queuesResult, wrapupCodesResult] = await Promise.all([
        fetchAllPages({
          pageSize: 100,
          fetchPage: ({ pageNumber, pageSize }) =>
            getQueues({
              pageSize,
              pageNumber,
              sortOrder: "ASC",
              divisionId: checkedDivisions,
            }),
        }),
        fetchAllPages({
          pageSize: 500,
          fetchPage: ({ pageNumber, pageSize }) =>
            getWrapupCodes({ pageSize, pageNumber, sortBy: "name", sortOrder: "ascending" }),
        }),
      ]);
      const wrapupTableQueues = await enrichQueuesWithWrapupCodes(queuesResult.entities || []);

      return {
        entities: wrapupCodesResult.entities || [],
        capped: queuesResult.capped || wrapupCodesResult.capped,
        context: { wrapupTableQueues },
      };
    },
    resolveColumns: ({ context }) => [
      { key: "wrapup_code_name", header: "Wrap Up Code" },
      ...buildWrapupQueueColumns(context.wrapupTableQueues || []),
      ...metadataColumns,
    ],
    mapRecord: (wrapupCode, context = {}) => {
      const row = { wrapup_code_name: wrapupCode.name };

      (context.wrapupTableQueues || []).forEach((queue) => {
        const isAssigned = (queue.wrapUpCodes || []).some((queueCode) => queueCode.id === wrapupCode.id);
        row[getWrapupQueueColumnKey(queue)] = isAssigned ? "x" : "";
      });

      return row;
    },
  },
  "auth-orgs-tab": {
    label: "Auth Orgs",
    fetchAll: unsupportedExport("Auth Orgs export needs nested trustee/trustor fan-out in the next implementation pass."),
  },
};

export function getExportAdapter(tabName) {
  return EXPORT_ADAPTERS[tabName];
}

export async function buildExportRows(adapter, context) {
  const result = await adapter.fetchAll(context);
  const exportContext = { ...context, ...(result.context || {}) };
  const entities = await enrichExportRecords(adapter, result.entities || [], exportContext);
  const mappedRows = result.rows || entities.flatMap((record) => adapter.mapRecord(record, exportContext));

  return {
    rows: mappedRows,
    capped: result.capped,
    columns: resolveExportColumns(adapter, mappedRows, exportContext, result),
  };
}

export async function buildExportRowsFromRecords(adapter, records, context = {}) {
  const entities = await enrichExportRecords(adapter, records || [], context);
  const rows = entities.flatMap((record) => adapter.mapRecord(record, context));

  return {
    rows,
    capped: false,
    columns: resolveExportColumns(adapter, rows, context),
  };
}

export function resolveExportColumns(adapter, rows, context = {}, result = {}) {
  if (result.columns) {
    return result.columns;
  }

  if (adapter.resolveColumns) {
    return adapter.resolveColumns({ rows, context, result });
  }

  return adapter.columns || [];
}

async function enrichExportRecords(adapter, records, context) {
  if (!adapter.enrichRecords) {
    return records;
  }

  return adapter.enrichRecords(records, context);
}

function buildUserSearchBody(filters = {}, pageNumber, pageSize) {
  const stateFilter = filters.state?.state === "any"
    ? {
      operator: "AND",
      type: "EXACT",
      fields: ["state"],
      values: ["active", "inactive", "deleted"],
    }
    : {
      operator: "AND",
      type: "EXACT",
      fields: ["state"],
      value: filters.state?.state || "active",
    };
  const body = {
    query: [stateFilter],
    sortOrder: "ASC",
    sortBy: "name",
    pageSize,
    pageNumber,
    expand: ["authorization", "station", "dateLastLogin", "team"],
  };

  if (filters.name) {
    body.query.push({
      operator: "AND",
      type: "CONTAINS",
      fields: ["name", "email"],
      value: filters.name,
    });
  }

  if (filters.checkedDivisions?.length) {
    body.query.push({
      operator: "AND",
      type: "EXACT",
      fields: ["divisionId"],
      values: filters.checkedDivisions,
    });
  }

  return body;
}

function buildCallRoutesOpts(filters = {}, pageNumber, pageSize) {
  const opts = {
    pageSize,
    pageNumber,
    sortBy: "name",
    sortOrder: "ASC",
  };

  if (filters.dnis) {
    opts.dnis = filters.dnis;
  }

  if (filters.name) {
    opts.name = `*${filters.name}*`;
  }

  return opts;
}

function buildPromptsOpts(filters = {}, pageNumber, pageSize) {
  const opts = {
    pageNumber,
    pageSize,
    name: [],
    sortBy: "name",
    sortOrder: "asc",
    includeMediaUris: true,
    includeResources: true,
  };

  if (filters.name) {
    opts.name.push(`*${filters.name}*`);
  }

  return opts;
}

function formatServiceLevel(serviceLevel) {
  if (!serviceLevel) {
    return "";
  }

  return `${serviceLevel.percentage * 100}% / ${serviceLevel.durationMs / 1000}s`;
}

function formatOnOff(value) {
  if (value === undefined || value === null) {
    return "";
  }

  return value ? "On" : "Off";
}

function formatSeconds(value) {
  if (value === undefined || value === null || value === "") {
    return "";
  }

  return `${value}s`;
}

function formatAcwTimeout(acwSettings = {}) {
  const prompt = acwSettings.wrapupPrompt || "";

  if (!acwSettings.timeoutMs) {
    return prompt;
  }

  return [prompt, `${acwSettings.timeoutMs / 1000}s`].filter(Boolean).join(" - ");
}

function formatOutboundEmailAddress(outboundEmailAddress) {
  const routePattern = outboundEmailAddress?.route?.pattern;
  const domainId = outboundEmailAddress?.domain?.id;

  return routePattern && domainId ? `${routePattern}@${domainId}` : "";
}

function formatYesBlank(value) {
  return value ? "YES" : "";
}

async function enrichGroupsForExport(groups, context = {}) {
  const canReadGroupRoles = permissionsToCheck("authorization:grant:view", context.permissions || []);

  return mapWithConcurrency(groups, async (group) => {
    const [grantsResult, membersResult] = await Promise.all([
      canReadGroupRoles && group.rolesEnabled ? getGroupRoles(group.id).catch(() => ({ grants: [] })) : Promise.resolve({ grants: [] }),
      fetchGroupMembers(group.id).catch(() => []),
    ]);

    return {
      ...group,
      exportGrants: grantsResult.grants || [],
      exportMembers: membersResult,
    };
  });
}

async function fetchGroupMembers(groupId) {
  const result = await fetchAllPages({
    pageSize: 100,
    fetchPage: ({ pageNumber, pageSize }) =>
      getGroupMembers(groupId, {
        pageSize,
        pageNumber,
        sortOrder: "ASC",
      }),
  });

  return result.entities || [];
}

function mapGroupForExport(group) {
  const groupFields = {
    group_name: group.name,
    description: group.description,
    calls_enabled: formatYesBlank(group.callsEnabled),
    group_id: group.id,
  };
  const roleRows = (group.exportGrants || []).map((grant) => ({
    ...groupFields,
    record_type: "Role",
    role_entity_name: grant.role?.name,
    division_name: grant.division?.id === "*" ? "All Divisions" : grant.division?.name,
  }));
  const ownerIds = new Set((group.owners || []).map((owner) => owner.id));
  const memberRows = (group.exportMembers || []).map((member) => ({
    ...groupFields,
    record_type: "Member",
    member_entity_name: member.name,
    owner: formatYesBlank(ownerIds.has(member.id)),
  }));
  const rows = [...roleRows, ...memberRows];

  return rows.length ? rows : [{ ...groupFields, record_type: "Summary" }];
}

async function enrichQueuesWithWrapupCodes(queues) {
  return mapWithConcurrency(queues, async (queue) => {
    if (Array.isArray(queue.wrapUpCodes)) {
      return queue;
    }

    const result = await fetchAllPages({
      pageSize: 500,
      fetchPage: ({ pageNumber, pageSize }) =>
        getQueueWrapupCodes(queue.id, {
          pageSize,
          pageNumber,
          sortBy: "name",
          sortOrder: "ascending",
        }),
    });

    return {
      ...queue,
      wrapUpCodes: result.entities || [],
    };
  }, 3);
}

function buildWrapupQueueColumns(queues) {
  const headerCounts = queues.reduce((counts, queue) => {
    const name = queue.name || "Unnamed Queue";
    counts[name] = (counts[name] || 0) + 1;
    return counts;
  }, {});

  return queues.map((queue) => {
    const header = queue.name && headerCounts[queue.name] > 1
      ? `${queue.name} (${String(queue.id).slice(0, 8)})`
      : queue.name || queue.id;

    return {
      key: getWrapupQueueColumnKey(queue),
      header,
    };
  });
}

function getWrapupQueueColumnKey(queue) {
  return `queue_${String(queue.id).replace(/[^a-z0-9_]/gi, "_")}`;
}

async function enrichScheduleGroupsForExport(scheduleGroups) {
  return mapWithConcurrency(scheduleGroups, async (scheduleGroup) => {
    if (scheduleGroup.status) {
      return scheduleGroup;
    }

    const status = await getScheduleGroupStatus(scheduleGroup);
    return { ...scheduleGroup, status };
  }, 4);
}

function formatScheduleNames(schedules = []) {
  return schedules.map((schedule) => schedule.name).filter(Boolean);
}

function formatScheduleGroupActive(scheduleGroup) {
  const status = scheduleGroup.status;

  if (!status) {
    return "";
  }

  if (!status.active) {
    return status.type || "Inactive";
  }

  const start = status.active.start?.slice(11, 16);
  const end = status.active.end?.slice(11, 16);
  const windowText = start && end ? `${start} - ${end}` : "";

  return [status.type, status.active.name, windowText].filter(Boolean).join(": ");
}